﻿import { useRef, useState } from 'react';
import { Banner, Button, Caption, Spinner } from '@telegram-apps/telegram-ui';
import { useImportUpload } from '../hooks/useImportUpload';
import { validateZipFile } from '../lib/importValidation';

function formatMb(bytes: number): string {
  return `${(bytes / (1024 * 1024)).toFixed(2)} МБ`;
}

export function ImportUploader() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useImportUpload();

  const onPickClick = () => {
    fileInputRef.current?.click();
  };

  const reset = () => {
    setSelectedFile(null);
    setInfo(null);
    setError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onFileChange = (fileList: FileList | null) => {
    setInfo(null);
    setError(null);
    const file = fileList?.[0] ?? null;
    if (!file) return;

    const check = validateZipFile(file);
    if (!check.ok) {
      setError(check.error);
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
  };

  const onUpload = async () => {
    if (!selectedFile) return;
    setInfo(null);
    setError(null);

    try {
      await upload.mutateAsync(selectedFile);
      setInfo('Импорт поставлен в очередь');
      reset();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'Не удалось загрузить ZIP';
      setError(msg);
    }
  };

  return (
    <div style={{ display: 'grid', gap: 10 }}>
      <input
        ref={fileInputRef}
        type="file"
        accept=".zip,application/zip"
        style={{ display: 'none' }}
        onChange={(e) => onFileChange(e.target.files)}
      />

      <Button mode="filled" size="m" stretched onClick={onPickClick} disabled={upload.isPending}>
        Выбрать ZIP
      </Button>

      {selectedFile && (
        <div style={{ display: 'grid', gap: 8 }}>
          <Caption level="1">
            Файл: {selectedFile.name} ({formatMb(selectedFile.size)})
          </Caption>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button mode="outline" size="s" onClick={reset} disabled={upload.isPending}>
              Отмена
            </Button>
            <Button mode="filled" size="s" onClick={() => void onUpload()} disabled={upload.isPending}>
              Загрузить
            </Button>
          </div>
        </div>
      )}

      {upload.isPending && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Spinner size="s" />
          <Caption level="1">Загружаем...</Caption>
        </div>
      )}

      {error && (
        <Banner
          type="section"
          header="Ошибка"
          subheader={error}
          style={{ border: '1px solid rgba(255, 59, 48, 0.35)' }}
        >
          <></>
        </Banner>
      )}

      {info && (
        <Banner type="section" header="Готово" subheader={info}>
          <></>
        </Banner>
      )}
    </div>
  );
}
